import { CashRegisterSession } from 'src/types/cashRegister';

/**
 * Vérifie si une session est ouverte
 */
export const isSessionOpen = (session: CashRegisterSession | null | undefined): boolean => {
  if (!session) return false;
  return session.status === 'open';
};

/**
 * Vérifie si une date est aujourd'hui
 */
export const isToday = (dateString: string | Date | undefined): boolean => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  const today = new Date();
  
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Vérifie si la session est du jour même
 * Retourne false si la session n'est pas du jour courant
 */
export const isSessionFromToday = (session: CashRegisterSession | null | undefined): boolean => {
  if (!session || !session.openedAt) return false;
  return isToday(session.openedAt);
};

/**
 * Vérifie si la session de caisse est valide pour les opérations
 * Une session est valide si:
 * 1. Elle existe
 * 2. Elle est ouverte (status === 'open')
 * 3. Elle a été ouverte aujourd'hui
 */
export const isSessionValidForOperations = (
  session: CashRegisterSession | null | undefined
): { valid: boolean; reason?: 'no_session' | 'closed' | 'different_day' } => {
  if (!session) {
    return { valid: false, reason: 'no_session' };
  }

  if (session.status !== 'open') {
    return { valid: false, reason: 'closed' };
  }

  if (!isSessionFromToday(session)) {
    return { valid: false, reason: 'different_day' };
  }

  return { valid: true };
};

/**
 * Formate un message d'erreur selon la raison d'invalidité de la session
 */
export const getSessionInvalidMessage = (
  reason: 'no_session' | 'closed' | 'different_day',
  t: (key: string) => string
): string => {
  switch (reason) {
    case 'no_session':
      return t('cash_register.errors.no_session');
    case 'closed':
      return t('cash_register.errors.session_closed');
    case 'different_day':
      return t('cash_register.errors.session_different_day');
    default:
      return t('cash_register.errors.session_invalid');
  }
};

/**
 * Détermine si l'utilisateur est un caissier
 * Un caissier a généralement un rôle spécifique ou une permission de caisse
 */
export const isCashierRole = (role: string | undefined): boolean => {
  if (!role) return false;
  const cashierRoles = ['caissier', 'cashier', 'Caissier', 'Cashier', 'CASHIER', 'CAISSIER'];
  return cashierRoles.includes(role);
};

/**
 * Type pour le résultat de validation de session
 */
export interface SessionValidationResult {
  needsAction: boolean;
  action: 'open' | 'close_reopen' | 'none';
  message: string;
  canProceed: boolean;
}

/**
 * Détermine l'action requise pour la session de caisse
 */
export const getRequiredSessionAction = (
  session: CashRegisterSession | null | undefined,
  t: (key: string) => string
): SessionValidationResult => {
  const validation = isSessionValidForOperations(session);

  if (validation.valid) {
    return {
      needsAction: false,
      action: 'none',
      message: '',
      canProceed: true,
    };
  }

  switch (validation.reason) {
    case 'no_session':
    case 'closed':
      return {
        needsAction: true,
        action: 'open',
        message: getSessionInvalidMessage(validation.reason!, t),
        canProceed: false,
      };
    case 'different_day':
      return {
        needsAction: true,
        action: 'close_reopen',
        message: t('cash_register.errors.session_must_close_reopen'),
        canProceed: false,
      };
    default:
      return {
        needsAction: true,
        action: 'open',
        message: t('cash_register.errors.session_invalid'),
        canProceed: false,
      };
  }
};
